import type { BallState } from './BallState';
import { PITCH_GEOMETRY, type PitchGeometry, type TeamSide } from './PitchGeometry';
import type { Vector2 } from './Vector2';

export interface OutOfBoundsRestart {
  type: BoundaryRestartType;
  teamSide: TeamSide;
  position: Vector2;
}

export type RestartType = 'THROW_IN' | 'CORNER' | 'GOAL_KICK' | 'FREE_KICK' | 'PENALTY';
export type BoundaryRestartType = Extract<RestartType, 'THROW_IN' | 'CORNER' | 'GOAL_KICK'>;

/** Pure rule decisions. State mutation and event emission remain in the resolver. */
export class GameRules {
  constructor(private readonly pitch: PitchGeometry = PITCH_GEOMETRY) {}

  isBallOut(position: Vector2): boolean {
    return this.pitch.isOutside(position);
  }

  outOfBoundsRestart(ball: BallState, fallbackPossession: TeamSide): OutOfBoundsRestart {
    const lastTouchTeam = ball.lastTouchTeam ?? fallbackPossession;
    const bounds = this.pitch.playableBounds;
    const horizontalOverrun = Math.max(bounds.minX - ball.position.x, ball.position.x - bounds.maxX, 0);
    const verticalOverrun = Math.max(bounds.minY - ball.position.y, ball.position.y - bounds.maxY, 0);
    if (verticalOverrun > horizontalOverrun) {
      return {
        type: 'THROW_IN',
        teamSide: lastTouchTeam === 0 ? 1 : 0,
        position: {
          x: Math.max(this.pitch.restartBounds.minX, Math.min(this.pitch.restartBounds.maxX, ball.position.x)),
          y: ball.position.y < this.pitch.centre.y ? this.pitch.restartBounds.minY : this.pitch.restartBounds.maxY,
        },
      };
    }

    const defendingSide: TeamSide = ball.position.x < this.pitch.centre.x ? 0 : 1;
    if (lastTouchTeam === defendingSide) {
      return {
        type: 'CORNER',
        teamSide: defendingSide === 0 ? 1 : 0,
        position: {
          x: ball.position.x < this.pitch.centre.x ? this.pitch.restartBounds.minX : this.pitch.restartBounds.maxX,
          y: ball.position.y < this.pitch.centre.y ? this.pitch.restartBounds.minY : this.pitch.restartBounds.maxY,
        },
      };
    }
    return {
      type: 'GOAL_KICK',
      teamSide: defendingSide,
      position: {
        x: this.pitch.worldX(defendingSide, this.pitch.goalArea.depth),
        y: this.pitch.centre.y,
      },
    };
  }

  teamAfterShotOut(shootingSide: TeamSide): TeamSide {
    return shootingSide === 0 ? 1 : 0;
  }

  goalKick(shootingSide: TeamSide): OutOfBoundsRestart {
    const teamSide = this.teamAfterShotOut(shootingSide);
    return {
      type: 'GOAL_KICK',
      teamSide,
      position: { x: this.pitch.worldX(teamSide, this.pitch.goalArea.depth), y: this.pitch.centre.y },
    };
  }

  teamAfterGoal(scoringSide: TeamSide): TeamSide {
    return scoringSide === 0 ? 1 : 0;
  }
}
